import { FC, PropsWithChildren, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Switch } from 'react-router-dom';

import classNames from 'classnames';
import ChatikIntegration from 'utils/chatik/ChatikIntegration';
import { CAMPAIGNS_DASHBOARD_PAGE_ROUTE } from 'utils/constants/app';
import { HHCDN_HOST, UXFB_WIDGET_SRC } from 'utils/constants/common';
import { Settings } from 'utils/constants/dynamicSettings';

import { SnackbarContextProvider } from '@hh.ru/magritte-ui';

import TipProvider from 'bloko/blocks/drop/Tip/Context';

import useSettings from 'hooks/useSettings';
import { useRequiredDataDownload } from 'utils/clickme/hooks/useRequiredDataDownload';

import routes, { NotFoundRoute, ROUTE_COMPONENTS } from 'app/routes';
import { ExternalRedirectRoute } from 'app/routes/ExternalRedirect/ExternalRedirect';
import { InternalRedirectRoute } from 'app/routes/InternalRedirect/InternalRedirect';
import { RouteType } from 'app/routes/routeType';
import UserRoute from 'app/routes/UserRoute';

import EmptyPage from 'pages/EmptyPage';

import Footer from 'components/Footer';
import LinearProgress from 'components/LinearProgress';
import QuizModal from 'components/QuizModal';
import RequisitesWarningModal from 'components/RequisitesWarningModal';
import Uxfeedback from 'components/Uxfeedback';

import AppError from 'components/common/ErrorBoundary/AppError';
import ErrorBoundary from 'components/common/ErrorBoundary/ErrorBoundary';
import { MagritteNotifications } from 'components/MagritteNotifications/MagritteNotifications';
import ClickmeManager from 'components/Notifications/ClickmeManager';
import { RequisitesFormModal } from 'components/RequisitesFormModal/RequisitesFormModal';
import WrongRequisitesModal from 'components/WrongRequisitesModal/WrongRequisitesModal';

import { getRoute } from 'store/middlewares/fetchRouter';
import { useSelector } from 'store/utils';

import 'components/Clickme/Clickme.less';

export const Clickme: FC<PropsWithChildren> = () => {
    const isLoad = useSelector((state) => state.loadPage);
    const location = useSelector((state) => state.router.location);
    const trls = useSelector((state) => state.translations.trls);
    const isDataDownloading = useRequiredDataDownload();
    const settings = useSettings();

    const enableUxfeedback = settings(Settings.EnableUxfeedback);

    useEffect(() => {
        if (trls['common.appName']) {
            document.title = `${trls['common.appName']} | ${trls[getRoute(location.pathname).title] || ''}`;
        }
    }, [location, trls]);

    if (isDataDownloading) return <EmptyPage />;

    return (
        <ChatikIntegration>
            <ErrorBoundary fallback={<AppError />}>
                <div
                    className={classNames('clickme', { 'xs-friendly': getRoute(location.pathname).xs })}
                    data-qa={isLoad ? 'clickme_rendering' : 'clickme_render_success'}
                >
                    <TipProvider>
                        <SnackbarContextProvider>
                            <>
                                <div className="clickme__content">
                                    <LinearProgress />
                                    <Switch location={location}>
                                        {routes.map((route) => {
                                            const { component, type, path, exact = true, to } = route;
                                            const RouteComponent = ROUTE_COMPONENTS[type];
                                            if (type === RouteType.ExternalRedirect && to) {
                                                return <ExternalRedirectRoute key={path} from={path} to={to} />;
                                            }
                                            if (type === RouteType.InternalRedirect && to) {
                                                return (
                                                    <InternalRedirectRoute
                                                        key={path}
                                                        exact={exact}
                                                        from={path}
                                                        to={to}
                                                    />
                                                );
                                            }
                                            return (
                                                <RouteComponent
                                                    key={path}
                                                    exact={exact}
                                                    path={path}
                                                    component={component}
                                                />
                                            );
                                        })}
                                        <UserRoute component={NotFoundRoute.component} />
                                    </Switch>
                                </div>
                                {(!isLoad || location.pathname.includes(CAMPAIGNS_DASHBOARD_PAGE_ROUTE)) && (
                                    <div className="clickme__footer">
                                        <Footer />
                                    </div>
                                )}
                                <ClickmeManager />
                                <MagritteNotifications />
                                <RequisitesFormModal />
                                <WrongRequisitesModal />
                                <RequisitesWarningModal />
                                <QuizModal />
                                {enableUxfeedback &&
                                    ReactDOM.createPortal(
                                        <Uxfeedback iframeSrc={UXFB_WIDGET_SRC} iframeOrigin={HHCDN_HOST} />,
                                        document.body
                                    )}
                            </>
                        </SnackbarContextProvider>
                    </TipProvider>
                </div>
            </ErrorBoundary>
        </ChatikIntegration>
    );
};
